import BranchAPIs from '@/API/branch-api'
import StorageService from '@/services/storage-service'
import { WEBAPP_URL } from '@/constants/config'
import LocalizationService from '@/services/localization-service'
import EpsonPrinter from '@/services/epson-print-service'
import locales from '@/constants/locales'
import MessagePacksApi from '@/API/marketing/message-packs-api'

export default {
  namespaced: true,
  state: {
    currentBranchId: StorageService.getItem('okmenu_branch', null),
    branches: [],
    fetchedSettings: {},
    multiBranchesModal: false,
    addBranchModal: false,
    addBranchTour: false,
    epsonPrinter: null,
    messages: {
      whats: 0,
      email: 0,
    },
  },
  getters: {
    branches: (state: any) => state.branches ?? [],
    messages: (state: any) => state.messages,
    enabledLanguagesForCurrentBranch: (state: any, getters: any) => locales.filter(el => (getters.currentBranch?.locales ?? []).includes(el.id)).map(el => ({ id: el.id, name: LocalizationService.translate(`locales.${el.id}`) })),
    currentBranchId: (state: any) => state.currentBranchId,
    epsonPrinter: (state: any) => state.epsonPrinter,
    ordersPrintMethod: (state: any, getters: any) => getters.currentBranch.print_settings?.method ?? getters.currentBranch.print_settings?.ordersPrintMethod ?? 'html',
    cashierSessionPrintMethod: (state: any, getters: any) => getters.currentBranch.print_settings?.cashierSessionPrintMethod ?? 'html',
    currentBranch: (state: any) => state.branches.find((branch: any) => branch.id === state.currentBranchId) ?? {},
    selectedBranch: (state: any) => (branchId: any) => state.branches.find((branch: any) => branch.id === (branchId ?? state.currentBranchId)) ?? {},
    isOldBranch: (state: any, getters: any) => getters.currentBranch?.from_old_dashboard ?? false,
    currentShifts: (state: any, getters: any) => getters.currentBranch.shifts,
    isCurrentBranchEnabledMultiVendor: (state: any, getters: any, rootState: any, rootGetters: any) => [1, 2].includes(rootGetters['integrations/activeVendorIntegration']?.status),
    currentBranchVendorStatus: (state: any, getters: any, rootState: any, rootGetters: any) => rootGetters['integrations/activeVendorIntegration']?.status,
    webappUrl: (state: any, getters: any) => `${WEBAPP_URL}/${getters.currentBranch?.reference_id}`,
    multiBranchesModal: (state: any) => state.multiBranchesModal,
    addBranchModal: (state: any) => state.addBranchModal,
    addBranchTour: (state: any) => state.addBranchTour,
    sellingTypesByBusiness: (state: any, getters: any, rootState: any, rootGetters: any) => (count = false) => {
      const isPos = rootGetters['menu/isPOS']
      if (isPos) {
        const connection = getters.currentBranch?.integrations.find((integration: any) => ['micros', 'foodics', 'marn'].includes(integration?.integration?.slug))
        const sellingTypes = connection?.config?.selling_types
        return Object.keys(sellingTypes).filter(type => !!sellingTypes[type])
          .reduce((cur, key) => Object.assign(cur, { [key]: sellingTypes[key] }), {})
      }
      const { features } = rootGetters['auth/user'].businessType
      const selling = getters.currentBranch?.selling_types ?? {}
      if (selling.dine_in || selling.dine_in_view) {
        selling.dine_in = 1
      }
      delete selling.dine_in_view
      const typesArray = Object.keys(selling)
      let typesCount = 0
      const types = {}
      typesArray.forEach((type: string) => {
        if (features[type] && selling[type]) {
          typesCount += 1
          // @ts-ignore
          types[type] = 1
        }
      })
      return count ? (typesCount === 1) : types
    },
  },
  mutations: {
    SET_BRANCHES(state: any, branches: Array<any>) {
      state.branches = branches
      if (!state.currentBranchId || !branches.find(b => b.id === state.currentBranchId)) {
        state.currentBranchId = branches[0]?.id
      }
      LocalizationService.setLocaleFromBranch(state.branches.find((branch: any) => branch.id === state.currentBranchId))
    },
    SET_BRANCH(state: any, branchId: number) {
      state.currentBranchId = branchId
      LocalizationService.setLocaleFromBranch(state.branches.find((branch: any) => branch.id === branchId))
    },
    SET_FETCHING_SETTINGS(state: any, branchId: number) {
      state.fetchedSettings[branchId] = true
    },
    RESET(state: any) {
      /*
      state.branches = []
      state.currentBranchId = null
     */
      state.fetchedSettings = {}
    },
    SET_ADD_BRANCH_MODAL(state: any, visible = true) {
      state.addBranchModal = visible
    },
    SET_MULTI_BRANCHES_MODAL(state: any, visible = true) {
      state.multiBranchesModal = visible
    },
    SET_ADD_BRANCH_TOUR(state: any, visible = true) {
      state.addBranchTour = visible
    },
    SET_PRINTER(state: any, printer: any) {
      state.epsonPrinter = printer
    },
    SET_MESSAGES(state: any) {
      MessagePacksApi.getStatisticsOfPacks().then(response => {
        const emailsTotalNumber = response.email.find((item: any) => item.label === 'remaining').count
        const whatsappTotalNumber = response.whatsapp.find((item: any) => item.label === 'remaining').count
        state.messages = { whatsapp: whatsappTotalNumber, email: emailsTotalNumber }
      })
    },
  },
  actions: {
    async FETCH_BRANCH_DETAILS(context: any, selectedBranchId?: any) {
      const branchId = selectedBranchId ?? context.state.currentBranchId
      const branchIdx = context.state.branches.findIndex((branch: any) => branch.id === branchId)
      if (branchIdx < 0 || context.state.fetchedSettings[branchId]) return context.state.branches?.[branchIdx]

      const branch = await BranchAPIs.GetBranchSettings(branchId)
      await context.dispatch('UPDATE_BRANCH', branch)
      context.commit('SET_FETCHING_SETTINGS', branchId)
      return branch
    },
    async FETCH_BRANCH_SHIFTS(context: any, selectedBranchId?: any) {
      const branch = selectedBranchId ? context.state.branches.find((item: any) => item.id === selectedBranchId) : context.getters.currentBranch
      if (branch?.shifts) return branch.shifts

      const shifts = await BranchAPIs.getShifts(branch?.id)
      await context.dispatch('UPDATE_BRANCH', { ...branch, shifts })
      return shifts
    },
    UPDATE_BRANCH(context: any, branch: any) {
      const branchIdx = context.state.branches.findIndex((b: any) => b.id === branch.id)
      const branches = [...context.state.branches]
      if (branchIdx >= 0) {
        branches[branchIdx] = {
          ...branches[branchIdx],
          ...branch,
        }
      } else {
        branches.push(branch)
      }
      context.commit('SET_BRANCHES', branches)
      return branch
    },
    DELETE_BRANCH(context: any, branch: any) {
      const branchIdx = context.state.branches.findIndex((b: any) => b.id === branch.id)
      const branches = [...context.state.branches]
      branches.splice(branchIdx, 1)
      context.commit('SET_BRANCHES', branches)
    },
    SET_BRANCH(context: any, branchId: number) {
      context.commit('SET_BRANCH', branchId)

      if (!context.rootGetters['auth/impersonate']) {
        StorageService.setItem('okmenu_branch', branchId)
      }
      return context.dispatch('CONNECT_PRINTER')
    },
    async CONNECT_PRINTER(context: any) {
      const settings = context.getters.currentBranch?.print_settings ?? {}
      if (settings.ordersPrintMethod === 'epson' || settings.cashierSessionPrintMethod === 'epson') {
        const printer = new EpsonPrinter(settings.ip, settings.port)
        await printer.connect()
        context.commit('SET_PRINTER', printer)
      } else {
        context.commit('SET_PRINTER', null)
      }
    },
  },
}
